// docs/185 PB13: the World chart as one continuous land of biome regions, Mario overworld style. This file lays the
// regions out (which block of the map each biome takes, and the ragged borders between them); `generateOverworld`
// then shapes each cell from its region's country and joins the regions by road. Pure and seeded, so every player
// in a World sees the same map, and the layout depends only on the World's seed and the set of biomes on it: a
// region unlocking never moves anything.
//
// The blocks: one row of regions for two or three biomes, two rows for four to eight (the Underdark always in the
// bottom row, so its entrance road can come down into it from the north). Each block is `REGION_W` × `REGION_H`
// cells, a row with fewer regions sharing the same width out between them. Borders wander up to `WARP` cells off
// the block's straight edge (smooth noise), so two countries meet along a ragged line, not a ruled one.
//
// The Underdark is sunken: a two-cell rim of its own rock runs round it wherever it meets another region (`cliff`),
// passable only at its gate, one column of the rim on its north side under the region its entrance road comes
// from. Its rivers stay inside it and no surface river falls into it.
import { BIOMES, type Biome } from './overworldBiomes';

/** One region the World's map should have: its biome, and whether the story has reached it yet. */
export interface RegionSpec { biome: Biome; unlocked: boolean }
/** A region as laid out on the map. */
export interface OwRegion {
  biome: Biome;
  /** Drawn veiled, its places hidden and the roads into it barred, until the story reaches it (PB13). */
  unlocked: boolean;
  /** Its block, in cells (`x1`, `y1` exclusive). The region's own cells are those `regionOf` names: the block, warped at its borders. */
  x0: number; y0: number; x1: number; y1: number;
  /** The region holding the World's seat (places with no biome stand here too). */
  home: boolean;
  /** The Underdark's way in: the rim's column the entrance road climbs down, and the region (index) it comes from. */
  gate?: { x: number; from: number };
}
export interface RegionPlan {
  w: number; h: number;
  regions: OwRegion[];
  /** The region (index) of each cell, row by row. */
  at: Uint8Array;
  /** The Underdark's rim: 1 a cell of its cliff, 2 a cell of its gate (the entrance road's pass). */
  cliff: Uint8Array;
}

export const REGION_W = 36, REGION_H = 22;
/** How far (cells) a border wanders off its block's straight edge. */
export const WARP = 3.5;
/** The warp's noise lattice (cells): over 3 × `WARP`, so the smoothstep's steepest slope keeps every border single. */
const WARP_STEP = 12;
/** How many regions each row holds, by how many there are. */
const ROWS: Record<number, readonly number[]> = { 2: [2], 3: [3], 4: [2, 2], 5: [3, 2], 6: [3, 3], 7: [4, 3], 8: [4, 4] };

export function mulberry(seed: number): () => number {
  let a = seed >>> 0;
  return () => { a = (a + 0x6d2b79f5) >>> 0; let t = a; t = Math.imul(t ^ (t >>> 15), t | 1); t ^= t + Math.imul(t ^ (t >>> 7), t | 61); return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };
}

/** Smooth value noise on a `step`-cell lattice, 0..1. */
export function noise(rand: () => number, w: number, h: number, step: number): (x: number, y: number) => number {
  const gw = Math.ceil(w / step) + 2, gh = Math.ceil(h / step) + 2;
  const g = Array.from({ length: gw * gh }, () => rand());
  const at = (i: number, j: number) => g[Math.max(0, Math.min(gh - 1, j)) * gw + Math.max(0, Math.min(gw - 1, i))]!;
  const s = (t: number) => t * t * (3 - 2 * t);
  return (x, y) => {
    const fx = x / step, fy = y / step, i = Math.floor(fx), j = Math.floor(fy), u = s(fx - i), v = s(fy - j);
    return (at(i, j) * (1 - u) + at(i + 1, j) * u) * (1 - v) + (at(i, j + 1) * (1 - u) + at(i + 1, j + 1) * u) * v;
  };
}

/** The distinct biomes asked for, in `BIOMES` order (so the layout never depends on the order they came in). */
export function regionBiomes(specs: readonly RegionSpec[]): Biome[] {
  const asked = new Set(specs.map((s) => s.biome));
  return BIOMES.filter((b) => asked.has(b));
}

/**
 * The map's regions for `specs`, or undefined when they hold one biome or none (the one-country chart, drawn as
 * before regions). `home` is the World's own biome: its region holds the seat (else the first spec's).
 */
export function planRegions(seed: number, specs: readonly RegionSpec[], home?: Biome): RegionPlan | undefined {
  const biomes = regionBiomes(specs);
  if (biomes.length <= 1) return undefined;
  // a stream of its own, so the layout is the World's whatever its places are
  const rand = mulberry((seed ^ 0x9e3779b9) >>> 0);
  const unlocked = (b: Biome) => specs.some((s) => s.biome === b && s.unlocked);
  const homeBiome = home && biomes.includes(home) ? home : specs[0]!.biome;
  const deep = biomes.includes('underdark');
  const n = biomes.length;
  const rows = deep && n === 2 ? [1, 1] : deep && n === 3 ? [2, 1] : ROWS[n]!;
  // the surface biomes shuffled, the Underdark into the bottom row
  const surface = biomes.filter((b) => b !== 'underdark');
  for (let i = surface.length - 1; i > 0; i--) { const j = Math.floor(rand() * (i + 1)); [surface[i], surface[j]] = [surface[j]!, surface[i]!]; }
  const last = rows[rows.length - 1]!;
  const deepAt = deep ? Math.floor(rand() * last) : -1;
  const cols = Math.max(...rows);
  const w = cols * REGION_W, h = rows.length * REGION_H;
  const regions: OwRegion[] = [];
  rows.forEach((count, row) => {
    for (let i = 0; i < count; i++) {
      const biome = row === rows.length - 1 && i === deepAt ? 'underdark' : surface.shift()!;
      const x0 = Math.round((i * w) / count), x1 = Math.round(((i + 1) * w) / count);
      regions.push({ biome, unlocked: unlocked(biome), x0, y0: row * REGION_H, x1, y1: (row + 1) * REGION_H, home: biome === homeBiome });
    }
  });
  // each cell's region: the block its warped position falls in
  // a lattice long enough that a border never folds back on itself (the warp's slope stays under 1: no enclaves)
  const nx = noise(rand, w, h, WARP_STEP), ny = noise(rand, w, h, WARP_STEP);
  const at = new Uint8Array(w * h);
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    const wx = x + (nx(x, y) - 0.5) * 2 * WARP, wy = y + (ny(x, y) - 0.5) * 2 * WARP;
    const row = Math.max(0, Math.min(rows.length - 1, Math.floor(wy / REGION_H)));
    let r = regions.findIndex((g) => g.y0 === row * REGION_H && wx < g.x1);
    if (r < 0) r = regions.findIndex((g) => g.y0 === row * REGION_H && g.x1 === w);
    at[y * w + x] = r;
  }
  // the Underdark's rim (two cells deep wherever it meets another region) and its gate under one region above it
  const cliff = new Uint8Array(w * h);
  const ud = regions.findIndex((g) => g.biome === 'underdark');
  if (ud >= 0) {
    for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
      if (at[y * w + x] !== ud) continue;
      let rim = false;
      for (let dy = -2; dy <= 2 && !rim; dy++) for (let dx = -2; dx <= 2 && !rim; dx++) {
        const X = x + dx, Y = y + dy;
        if (X >= 0 && Y >= 0 && X < w && Y < h && at[Y * w + X] !== ud) rim = true;
      }
      if (rim) cliff[y * w + x] = 1;
    }
    const g = regions[ud]!;
    const above = regions.map((r, i) => [r, i] as const).filter(([r]) => r.y1 === g.y0 && r.x1 > g.x0 + 10 && r.x0 < g.x1 - 10);
    const [from, fi] = above[Math.floor(rand() * above.length)]!;
    // the column where the rim is thinnest (its border flattest), in the middle of the stretch the two share
    const lo = Math.max(g.x0, from.x0) + 6, hi = Math.min(g.x1, from.x1) - 6;
    let best: number[] = [], thin = 99;
    for (let x = lo; x < hi; x++) {
      let n2 = 0;
      for (let y = 0; y < h; y++) if (cliff[y * w + x]) n2++;
      if (n2 < thin) { thin = n2; best = [x]; } else if (n2 === thin) best.push(x);
    }
    const gx = best[Math.floor(rand() * best.length)] ?? Math.floor((lo + hi) / 2);
    for (let y = g.y0 - REGION_H; y < g.y1; y++) if (y >= 0 && cliff[y * w + gx]) cliff[y * w + gx] = 2;
    g.gate = { x: gx, from: fi };
  }
  return { w, h, regions, at, cliff };
}

/** The cell's region (index), off the map's edge the nearest cell's. */
export function regionAt(plan: Pick<RegionPlan, 'w' | 'h' | 'at'>, x: number, y: number): number {
  const X = Math.max(0, Math.min(plan.w - 1, x)), Y = Math.max(0, Math.min(plan.h - 1, y));
  return plan.at[Y * plan.w + X]!;
}

/**
 * The map's regions from the module's public `world_campaign` row (docs/185 PB5, PB13): `biomes` every biome any path
 * of the campaign reaches (each a region on the map), `regions` those the story has reached (unlocked). Undefined
 * without a row (an AI World): the one-country chart. A biome the client doesn't know is left off.
 */
export function campaignRegions(row: { biomes: readonly string[]; regions: readonly string[] } | undefined): RegionSpec[] | undefined {
  if (!row || row.biomes.length === 0) return undefined;
  const known = new Set<string>(BIOMES);
  return row.biomes.filter((b): b is Biome => known.has(b)).map((biome) => ({ biome, unlocked: row.regions.includes(biome) }));
}

/** `?regions=temperate,desert:locked` (review only): a map of regions, `:locked` for one the story has not reached. */
export function parseRegionsPin(s: string | null | undefined): RegionSpec[] | undefined {
  if (!s) return undefined;
  const known = new Set<string>(BIOMES);
  const all = s === 'all' ? BIOMES.map((b) => ({ biome: b, unlocked: true })) : s.split(',').map((t) => t.trim().split(':')).filter(([b]) => known.has(b ?? '')).map(([b, l]) => ({ biome: b as Biome, unlocked: l !== 'locked' }));
  return all.length ? all : undefined;
}
